import { useCallback } from 'react';

import { useRouter } from 'next/router';

import {
  AnalyticsEvent,
  SearchEvents,
  ShoppingEvents,
  useAnalytics,
} from '@storefront-core/analytics-web';

import {
  getSessionData,
  SessionStorageAnalyticProps,
  SourceEvent,
} from '@/utils/analytics.util';
import {
  getPageNameFromPath,
} from '@/utils/getPageNameFromPath.util';

type SearchPerformPayload = {
  query?: string;
  personalised?: boolean;
  page: number;
  autocorrectedQuery?: string;
  isAutoCorrection?: boolean;
  isAutocategorisation?: boolean;
  autoCategorisedCategory?: string;
  sourceEvent?: string;
  triggerType?: string;
  isAutocategorisedOrBrandBoosted?: string;
  sortBy?: string;
  recordFound?: string[];
};

type ListSearchClickPayload = {
  sourceEvent: SourceEvent.ICON | SourceEvent.MENU;
};

export const useAnalyticsEvents = () => {
  const { trackEvent } = useAnalytics();

  const router = useRouter();
  const pathname = router.asPath || '';
  const locale =
    typeof router.query.lang === 'string' ? router.query.lang : undefined;
  const getComputedPageName = useCallback(
    () =>
      getPageNameFromPath({
        pathname,
        locale,
      }),
    [pathname, locale]
  );

  const trackSearchStartClick = () => {
    trackEvent(AnalyticsEvent.searchStart, {
      pageName: getComputedPageName(),
    });
  };

  const trackSearchPerform = useCallback(
    (props: SearchPerformPayload) => {
      const { ...restProps } = props;
      const sessionData = getSessionData(
        SessionStorageAnalyticProps.SEARCH_PERFORM_ANALYTIC_PROPS
      );
      const sessionFilterData = getSessionData(
        SessionStorageAnalyticProps.FILTER_PERFORM_ANALYTIC_PROPS
      );
      const analytics = sessionData ? JSON.parse(sessionData) : null;
      const filterAnalytics = sessionFilterData
        ? JSON.parse(sessionFilterData)
        : null;
      const querySuggestionSelectedIndex =
        analytics?.querySuggestionSelectedIndex;
      const shouldUseTypedQuery =
        analytics?.sourceEvent === SourceEvent.QUERY_SUGGESTIONS &&
        typeof analytics?.typedQuery === 'string' &&
        analytics.typedQuery.trim().length > 0;
      const effectiveQuery = shouldUseTypedQuery
        ? analytics.typedQuery
        : restProps.query;
      const properties = {
        pageName: analytics?.pageName ?? getComputedPageName(),
        querySuggestionRecordFound: analytics?.querySuggestionRecordFound,
        querySuggestionFound: analytics?.querySuggestionRecordFound?.length,
        ...(analytics?.querySuggestionSelected && {
          querySuggestionSelected: analytics?.querySuggestionSelected,
        }),
        ...(querySuggestionSelectedIndex !== undefined && {
          querySuggestionSelectedIndex,
        }),
        ...(analytics?.sponsored && {
          sponsored: analytics.sponsored,
        }),
        sourceEvent: analytics?.sourceEvent,
        triggerType: analytics?.triggerType,
        ...restProps,
        query: effectiveQuery,
        ...filterAnalytics,
      };

      trackEvent(SearchEvents.searchPerform, properties);
    },
    [getComputedPageName, trackEvent]
  );

  const trackPdpClick = () => {
    const clickSessionData = getSessionData(
      SessionStorageAnalyticProps.PDP_CLICK_ANALYTIC_PROPS
    );
    const clickAnalytics = clickSessionData
      ? JSON.parse(clickSessionData)
      : null;
    if (!clickAnalytics) return;
    const properties = {
      ...clickAnalytics,
      pageName: clickAnalytics?.pageName ?? getComputedPageName(),
    };
    trackEvent(ShoppingEvents.productCarouselItemClick, properties);
  };

  const trackAddToBasket = (eventName: string) => {
    const sessionData = getSessionData(
      SessionStorageAnalyticProps.ADD_TO_BASKET_ANALYTIC_PROPS
    );
    const analytics = sessionData ? JSON.parse(sessionData) : null;
    if (!analytics) return;
    const properties = {
      pageName: analytics?.pageName ?? getComputedPageName(),
      ...analytics,
    };
    trackEvent(eventName, properties);
  };

  const trackListSearchClick = (props: ListSearchClickPayload) => {
    trackEvent(AnalyticsEvent.listSearchClick, {
      pageName: getComputedPageName(),
      ...props,
    });
  };

  const trackMultiSearchPerformClick = useCallback(
    (isNative: boolean) => {
      if (!isNative) return;
      const sessionData = getSessionData(
        SessionStorageAnalyticProps.MULTI_SEARCH_ANALYTIC_PROPS
      );
      const analytics = sessionData ? JSON.parse(sessionData) : null;
      trackEvent(AnalyticsEvent.multiListSearchPerform, {
        pageName: getComputedPageName(),
        ...analytics,
      });
    },
    [trackEvent, getComputedPageName]
  );

  const trackMultiSearchClearAllClick = (isNative: boolean) => {
    if (!isNative) return;
    trackEvent(AnalyticsEvent.listSearchClear, {
      pageName: getComputedPageName(),
    });
  };

  const trackListSearchDismissedClick = (isNative: boolean) => {
    if (!isNative) return;
    trackEvent(AnalyticsEvent.listSearchDismissed, {
      pageName: getComputedPageName(),
    });
  };

  const trackCarouselImpression = (title:string, sourceEvent:string, query:string, productIds: string[]= []) => {
    const impressionAnalytics = {
        carouselName: title,
        sourceEvent,
        products: productIds,
        query
    };
    const properties = {
      pageName: getComputedPageName(),
      ...impressionAnalytics,
    };
    trackEvent(ShoppingEvents.productCarouselImpression, properties);
  };

  const trackCarouselScroll = (title:string, sourceEvent:string, query:string) => {
    const impressionAnalytics = {
        carouselName: title,
        sourceEvent,
        query
    };
    const properties = {
      pageName: getComputedPageName(),
      ...impressionAnalytics,
    };
    trackEvent(ShoppingEvents.productCarouselSlide, properties);
  };

  return {
    getComputedPageName,
    trackSearchStartClick,
    trackSearchPerform,
    trackPdpClick,
    trackAddToBasket,
    trackListSearchClick,
    trackMultiSearchPerformClick,
    trackMultiSearchClearAllClick,
    trackListSearchDismissedClick,
    trackCarouselScroll,
    trackCarouselImpression
  };
};
